import {
  locationIdFormat,
  productIdFormat,
  storageLocationIdFormat,
  transferSessionIdFormat,
} from "@redotech/redo-ids/ids";
import {
  OpensearchPaginationCursorSchema,
  SortDirection,
} from "@redotech/redo-model/tables/table";
import {
  AdvancedFilterDataSchema,
  FilterGroupSchema,
  VALID_PAGE_SIZES,
} from "@redotech/redo-model/views/views";
import { ReplenishmentRequestStatusSchema } from "@redotech/redo-model/wms/schema/replenishment-request";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { TransferSessionStateSchema } from "./transfer-session";

// GET - Dashboard: urgent SKU count
export const getReplenishmentDashboard = {
  name: "getReplenishmentDashboard" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({ urgentSkuCount: z.number().int().nonnegative() }),
};

export type GetReplenishmentDashboardInput = z.infer<
  typeof getReplenishmentDashboard.input
>;

// POST - Create replenishment route (transfer session)
const UnsourcedProductSchema = z.object({
  productId: productIdFormat.schema,
  storageLocationId: storageLocationIdFormat.schema,
  productTitle: z.string(),
  sku: z.string().nullable(),
});

export const createReplenishmentRoute = {
  name: "createReplenishmentRoute" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    skuCount: z.number().int().min(1).max(99),
  }),
  output: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    unsourcedProducts: z.array(UnsourcedProductSchema),
  }),
};

export type UnsourcedProduct = z.infer<typeof UnsourcedProductSchema>;

export type CreateReplenishmentRouteInput = z.infer<
  typeof createReplenishmentRoute.input
>;

// POST - Create replenishment route from specific request IDs
export const createReplenishmentRouteFromRequests = {
  name: "createReplenishmentRouteFromRequests" as const,
  input: z.object({ requestIds: z.array(z.string()).min(1) }),
  output: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    unsourcedProducts: z.array(UnsourcedProductSchema),
    locationId: locationIdFormat.schema,
  }),
};

export type CreateReplenishmentRouteFromRequestsInput = z.infer<
  typeof createReplenishmentRouteFromRequests.input
>;

// POST - Dismiss replenishment request(s) for a specific bin+product
export const dismissReplenishmentRequest = {
  name: "dismissReplenishmentRequest" as const,
  input: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    storageLocationId: storageLocationIdFormat.schema,
    productId: productIdFormat.schema,
    note: z.string().optional(),
  }),
  output: z.object({
    dismissedCount: z.number().int().nonnegative(),
    transferState: TransferSessionStateSchema,
  }),
};

export type DismissReplenishmentRequestInput = z.infer<
  typeof dismissReplenishmentRequest.input
>;

// POST - Fail replenishment request(s) for a specific source bin+product (no backstock found)
export const failReplenishmentRequest = {
  name: "failReplenishmentRequest" as const,
  input: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    storageLocationId: storageLocationIdFormat.schema,
    productId: productIdFormat.schema,
    note: z.string().optional(),
  }),
  output: z.object({
    failedCount: z.number().int().nonnegative(),
    transferState: TransferSessionStateSchema,
  }),
};

export type FailReplenishmentRequestInput = z.infer<
  typeof failReplenishmentRequest.input
>;

// POST - Release a replenishment session for pickup by another user
export const releaseReplenishmentSession = {
  name: "releaseReplenishmentSession" as const,
  input: z.object({ transferSessionId: transferSessionIdFormat.schema }),
  output: z.object({ success: z.boolean() }),
};

export type ReleaseReplenishmentSessionInput = z.infer<
  typeof releaseReplenishmentSession.input
>;

// GET - List sourced replenishment sessions available for pickup
const SourcedReplenishmentSessionSchema = z.object({
  transferSessionId: transferSessionIdFormat.schema,
  products: z.array(
    z.object({
      productId: productIdFormat.schema,
      sku: z.string().nullable(),
      title: z.string(),
    }),
  ),
});

export const listSourcedReplenishments = {
  name: "listSourcedReplenishments" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({ sessions: z.array(SourcedReplenishmentSessionSchema) }),
};

export type ListSourcedReplenishmentsInput = z.infer<
  typeof listSourcedReplenishments.input
>;

// GET - List in-progress sourcing replenishment sessions
export const listInProgressSourcingSessions = {
  name: "listInProgressSourcingSessions" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({ sessions: z.array(SourcedReplenishmentSessionSchema) }),
};

export type ListInProgressSourcingSessionsInput = z.infer<
  typeof listInProgressSourcingSessions.input
>;

// POST - Claim a sourced replenishment session for distribution
export const claimSourcedReplenishment = {
  name: "claimSourcedReplenishment" as const,
  input: z.object({ transferSessionId: transferSessionIdFormat.schema }),
  output: z.object({ success: z.boolean() }),
};

export type ClaimSourcedReplenishmentInput = z.infer<
  typeof claimSourcedReplenishment.input
>;

// POST - Specify source bin for an unsourced replenishment product
export const specifyReplenishmentSource = {
  name: "specifyReplenishmentSource" as const,
  input: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    productId: productIdFormat.schema,
    destinationStorageLocationId: storageLocationIdFormat.schema,
    sourceBinName: z.string().min(1),
  }),
  output: z.object({
    success: z.boolean(),
    transferState: TransferSessionStateSchema,
  }),
};

export type SpecifyReplenishmentSourceInput = z.infer<
  typeof specifyReplenishmentSource.input
>;

// GET - List unsourced products for a replenishment session
export const getUnsourcedReplenishmentProducts = {
  name: "getUnsourcedReplenishmentProducts" as const,
  input: z.object({ transferSessionId: transferSessionIdFormat.schema }),
  output: z.object({ unsourcedProducts: z.array(UnsourcedProductSchema) }),
};

export type GetUnsourcedReplenishmentProductsInput = z.infer<
  typeof getUnsourcedReplenishmentProducts.input
>;

// GET - List open replenishment requests for the table view
const ReplenishmentRequestRowSchema = z.object({
  id: z.string(),
  storageLocationId: storageLocationIdFormat.schema,
  binName: z.string(),
  productId: productIdFormat.schema,
  productTitle: z.string().nullable(),
  sku: z.string().nullable(),
  status: ReplenishmentRequestStatusSchema,
  sourceStorageLocationId: storageLocationIdFormat.schema.nullable(),
  sourceBinName: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
  unpickedShipmentCount: z.number().int().nonnegative(),
  unpickedItemTotal: z.number().nonnegative(),
});

export const listReplenishmentRequests = {
  name: "listReplenishmentRequests" as const,
  input: z.object({
    pageNumber: z.number().int().nonnegative(),
    pageSize: z.number().int().min(1),
    search: z.string().optional(),
  }),
  output: z.object({
    requests: z.array(ReplenishmentRequestRowSchema),
    total: z.number().int().nonnegative(),
  }),
};

export type ReplenishmentRequestTableRow = z.infer<
  typeof ReplenishmentRequestRowSchema
>;

export type ListReplenishmentRequestsInput = z.infer<
  typeof listReplenishmentRequests.input
>;

// GET - Search open replenishment requests (OpenSearch-backed). Returns rows
// plus an opensearch pagination cursor; total count comes from
// getReplenishmentRequestsCount.
export const searchReplenishmentRequests = {
  name: "searchReplenishmentRequests" as const,
  input: z.object({
    pageNumber: z.number().int().nonnegative(),
    pageSize: z
      .number()
      .refine((value) => VALID_PAGE_SIZES.includes(value as any), {
        message: `pageSize must be one of: ${VALID_PAGE_SIZES.join(", ")}`,
      }),
    search: z.string().nullish(),
    sort: z
      .object({ key: z.string(), direction: createEnum(SortDirection) })
      .nullish(),
    filterGroup: FilterGroupSchema.nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
  output: z.object({
    requests: z.array(ReplenishmentRequestRowSchema),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
};

export type SearchReplenishmentRequestsInput = z.infer<
  typeof searchReplenishmentRequests.input
>;

// GET - Count of open replenishment requests matching the given filters.
export const getReplenishmentRequestsCount = {
  name: "getReplenishmentRequestsCount" as const,
  input: z.object({
    search: z.string().nullish(),
    filterGroup: FilterGroupSchema.nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
  }),
  output: z.object({ total: z.number().int().nonnegative() }),
};

export type GetReplenishmentRequestsCountInput = z.infer<
  typeof getReplenishmentRequestsCount.input
>;

// GET - Distinct SKU values across the caller's open replenishment requests.
// Used to populate the SKU advanced-filter dropdown.
export const getReplenishmentRequestDistinctSkus = {
  name: "getReplenishmentRequestDistinctSkus" as const,
  input: z.object({}),
  output: z.object({ skus: z.array(z.string()) }),
};

// POST - Manually create a single replenishment request. Returns the new
// request id, or throws if a pending/in-progress request already exists for
// the same (bin, product) pair.
export const createReplenishmentRequest = {
  name: "createReplenishmentRequest" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    productId: productIdFormat.schema,
    storageLocationId: storageLocationIdFormat.schema,
    sourceStorageLocationId: storageLocationIdFormat.schema.nullish(),
    note: z.string().nullish(),
  }),
  output: z.object({ id: z.string() }),
};

export type CreateReplenishmentRequestInput = z.infer<
  typeof createReplenishmentRequest.input
>;

// GET - Distinct destination-bin names across the caller's open replenishment
// requests. Used to populate the bin advanced-filter dropdown.
export const getReplenishmentRequestDistinctBinNames = {
  name: "getReplenishmentRequestDistinctBinNames" as const,
  input: z.object({}),
  output: z.object({ binNames: z.array(z.string()) }),
};

// POST - Cancel a pending replenishment request by ID
export const cancelReplenishmentRequest = {
  name: "cancelReplenishmentRequest" as const,
  input: z.object({ requestId: z.string() }),
  output: z.object({ success: z.boolean() }),
};

export type CancelReplenishmentRequestInput = z.infer<
  typeof cancelReplenishmentRequest.input
>;

// POST - Cancel multiple pending replenishment requests by ID
export const cancelReplenishmentRequests = {
  name: "cancelReplenishmentRequests" as const,
  input: z.object({ requestIds: z.array(z.string()).min(1) }),
  output: z.object({ cancelledCount: z.number() }),
};

export type CancelReplenishmentRequestsInput = z.infer<
  typeof cancelReplenishmentRequests.input
>;
